/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.15.0
 */

import React, { Fragment, useCallback, useEffect, useState } from 'react';

import includes from 'lodash/includes';
import Joyride, { ACTIONS, CallBackProps, EVENTS, TooltipRenderProps } from 'react-joyride';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectOnboardingTranslation } from '@modules/internationalization/intSelectors';
import { IOnboardingTranslation } from '@modules/internationalization/types';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import {
    makeSelectAuthOnboardingEnabled,
    makeSelectAuthoritiesIsServiceUser,
} from '@pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { useOnboardingElementsConfiguration } from '../hooks/useOnboardingElementsConfiguration';
import {
    onboardingDisableAction,
    onboardingFinishAction,
    onboardingNextStepAction,
    onboardingSetCurrentStepAction,
    onboardingSkipAction,
    onboardingStartAction,
} from '../onboardingActions';
import { IOnboardingReducer } from '../onboardingReducer';
import {
    makeSelectOnboardingIsDisplayedByUser,
    makeSelectOnboardingModalCurrentStep,
    makeSelectOnboardingModalFinishId,
    makeSelectOnboardingModalStartId,
    makeSelectOnboardingPage,
    makeSelectOnboardingStatus,
    makeSelectOnboardingSteps,
} from '../onboardingSelectors';
import {
    IOnboardingNextStepPayload,
    IOnboardingSetCurrentStepPayload,
    TOnboardingFinishPayload,
} from '../types/onboardingActionTypes';
import {
    EOnboardingComponentsId,
    EOnboardingModalId,
    EOnboardingPage,
    EOnboardingStatus,
    IOnboardingStep,
} from '../types/onboardingTypes';
import formatOnboardingSelector from '../utils/formatOnboardingSelector';
import { onboardingElementsConfiguration } from '../utils/onboardingElementsConfiguration';
import { onboardingSetSpotlightHeight } from '../utils/onboardingSetSpotlightHeight';
import { onboardingModals } from './modal/onboardingModals';
import { useOnboardingModal } from './modal/useOnboardingModal';
import OnboardingTooltip from './OnboardingTooltip';

const Onboarding = ({
    currentStep,
    isDisplayedByUser,
    isOnboardingEnabled,
    isOnboardingFeatureEnabled,
    isServiceUser,
    modalFinalId,
    modalStartId,
    onboardingFinish,
    onboardingNextStep,
    onboardingSetCurrentStep,
    onboardingDisable,
    onboardingSkip,
    onboardingStart,
    openModal,
    page,
    status,
    stepsIds,
    translationOnboarding,
}: IDispatchProps & IConnectedProps) => {
    const { nextStep, skip } = useOnboardingModal(EOnboardingModalId.modalWelcome);

    const [steps, setSteps] = useState<IOnboardingStep[]>([]);

    const onboardingPageConfiguration = useOnboardingElementsConfiguration();

    const pagesDisabledScroll = [
                EOnboardingPage.inquiries,
            ];

    const disableScrolling = includes(pagesDisabledScroll, page);

    const onScrollChange = useCallback(() => {
        if (currentStep && !onboardingElementsConfiguration[currentStep].flexibleScroll) {
            return;
        }
        const querySelector = currentStep ? document.querySelector(formatOnboardingSelector(currentStep)) : null;
        if (querySelector && currentStep && status === EOnboardingStatus.start) {
            const top = querySelector.getBoundingClientRect().top;
            const height =
                querySelector.getBoundingClientRect().height +
                (onboardingElementsConfiguration[currentStep]?.spotlightPadding ?? 10) * 2;
            const offsetBottom = onboardingPageConfiguration[page].offsetBottom;

            onboardingSetSpotlightHeight(top, offsetBottom, height);
        }
    }, [currentStep, status, onboardingPageConfiguration, page]);

    const handleJoyrideCallback = useCallback(
        (data: CallBackProps) => {
            const { action, index, type } = data;

            const step = data.step as IOnboardingStep;

            if (type === EVENTS.TOUR_END) {
                // досрочное прекращение онбординга
                if (action === ACTIONS.SKIP) {
                    onboardingSkip();
                    onboardingDisable();
                } else {
                    // пройдены все шаги онбординга
                    const Component = modalFinalId ? onboardingModals[modalFinalId] : undefined;
                    if (Component) {
                        openModal({
                            onCloseCallback: onboardingFinish,
                            // @ts-ignore
                            render: (p) => <Component onCloseModal={p.onCloseModal} />,
                        });
                    }
                }
            } else if (type === EVENTS.STEP_AFTER) {
                const i = action !== ACTIONS.CLOSE ? index - 1 : index + 1;
                onboardingNextStep({
                    alreadySeen: action !== ACTIONS.CLOSE,
                    completeId: step.stepId,
                    id: steps[i]?.stepId,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [steps, modalFinalId, openModal, onboardingFinish, onboardingSkip, onboardingNextStep],
    );

    useEffect(() => {
        if (isServiceUser) return;

        setSteps(
            stepsIds
                .filter((stepId: EOnboardingComponentsId) => !!document.querySelector(formatOnboardingSelector(stepId)))
                .map((stepId: EOnboardingComponentsId) => ({
                    target: formatOnboardingSelector(stepId),
                    content: '',
                    disableBeacon: true,
                    offset: 5,
                    floaterProps: {
                        disableAnimation: true,
                    },
                    ...onboardingElementsConfiguration[stepId],

                    stepId,
                    link: {
                        ...translationOnboarding.tooltip.link.default,
                        ...translationOnboarding.elements[stepId].link,
                    },
                    page: translationOnboarding.tooltip.page,
                    text: translationOnboarding.elements[stepId].text,
                })),
        );
    }, [isServiceUser, stepsIds, translationOnboarding]);

    useEffect(() => {
        if (steps.length) {
            onboardingSetCurrentStep({ id: steps[0].stepId });
            const pagesWithoutScroll = [
                EOnboardingPage.accounts,
                EOnboardingPage.subscriberMobile,
                EOnboardingPage.inquiries,
            ];
            if (pagesWithoutScroll.includes(page) && status === EOnboardingStatus.start) {
                document.body.classList.add('show-onboarding');
            } else {
                document.body.classList.remove('show-onboarding');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, status, steps, onboardingSetCurrentStep]);

    useEffect(() => {
        document.addEventListener('scroll', onScrollChange);
        if (status !== EOnboardingStatus.start) {
            document.removeEventListener('scroll', onScrollChange);
        }
        return () => {
            document.removeEventListener('scroll', onScrollChange);
        };
    }, [status, onScrollChange]);

    useEffect(() => {
        setTimeout(() => {
            onScrollChange();
        });
    }, [onScrollChange]);

    useEffect(() => {
        if (isServiceUser) return;

        const Component = modalStartId ? onboardingModals[modalStartId] : undefined;
        if (status === EOnboardingStatus.pageReady) {
            if (stepsIds.length === 0) {
                onboardingFinish({ noSendRequest: true });
                return;
            }

            if (Component) {
                openModal({
                    onBackgroundCloseCallback: nextStep,
                    onCrossClickCallback: skip,
                    // @ts-ignore
                    render: () => <Component />,
                });
            } else {
                onboardingStart();
            }
        }
    }, [isServiceUser, status, modalStartId, openModal, onboardingStart, skip, nextStep, stepsIds, onboardingFinish]);

    if (
        (!isOnboardingEnabled && !isDisplayedByUser) ||
        isServiceUser ||
        (!isOnboardingFeatureEnabled && !isDisplayedByUser)
    ) {
        return <Fragment />;
    }

    return (
        <Fragment>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.onboarding]} />

            {/*<OnboardingTooltip />*/}
            <Joyride
                callback={handleJoyrideCallback}
                run={status === EOnboardingStatus.start}
                scrollOffset={onboardingPageConfiguration[page].offset}
                steps={steps}
                styles={{
                    options: {
                        overlayColor: '#33333369',
                    },
                }}
                // @ts-ignore
                tooltipComponent={OnboardingTooltip as React.ElementType<TooltipRenderProps>}

                // eslint-disable-next-line react/jsx-sort-props
                disableScrolling={disableScrolling}
            />
        </Fragment>
    );
};

type TReducer = Pick<
    IOnboardingReducer,
    'currentStep' | 'isDisplayedByUser' | 'modalFinalId' | 'modalStartId' | 'page' | 'status' | 'stepsIds'
>;

export interface IConnectedProps extends TReducer {
    isOnboardingEnabled: boolean;
    isOnboardingFeatureEnabled: boolean;
    isServiceUser: boolean;
    translationOnboarding: IOnboardingTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    currentStep: makeSelectOnboardingModalCurrentStep(),
    isDisplayedByUser: makeSelectOnboardingIsDisplayedByUser(),
    isOnboardingEnabled: makeSelectAuthOnboardingEnabled(),
    isOnboardingFeatureEnabled: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.onboarding),
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
    modalFinalId: makeSelectOnboardingModalFinishId(),
    modalStartId: makeSelectOnboardingModalStartId(),
    page: makeSelectOnboardingPage(),
    status: makeSelectOnboardingStatus(),
    stepsIds: makeSelectOnboardingSteps(),
    translationOnboarding: makeSelectOnboardingTranslation(),
});

export interface IDispatchProps {
    onboardingFinish: (payload?: TOnboardingFinishPayload) => void;
    onboardingNextStep: (payload: IOnboardingNextStepPayload) => void;
    onboardingSetCurrentStep: (payload: IOnboardingSetCurrentStepPayload) => void;
    onboardingDisable: () => void;
    onboardingSkip: () => void;
    onboardingStart: () => void;
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    onboardingFinish: (payload) => dispatch(onboardingFinishAction(payload)),
    onboardingNextStep: (payload) => dispatch(onboardingNextStepAction(payload)),
    onboardingSetCurrentStep: (payload) => dispatch(onboardingSetCurrentStepAction(payload)),
    onboardingDisable: () => dispatch(onboardingDisableAction()),
    onboardingSkip: () => dispatch(onboardingSkipAction()),
    onboardingStart: () => dispatch(onboardingStartAction()),
    openModal: (payload) => dispatch(modalOpen(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Onboarding);
